import type { MoneyMapNewFruitfulAccount } from '@fruitful/types';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    isNonMobileWeb,
    redactPrivate,
    XStack,
    YStack,
} from 'smoothie';

import { preferLegalName } from '../../../common/utils/bankingUtils';
import {
    toCardFriendlyName,
    truncateCardFriendlyName,
} from '../../../common/utils/cardUtils';
import { type User } from '../../../user/types/User';

const TitleText = isNonMobileWeb ? BodyMediumEmphasized : BodySmallEmphasized;
const Text = isNonMobileWeb ? BodyMediumRegular : BodySmallRegular;

export const PhysicalCardDetails = ({ user }: { user: User }) => {
    const [firstName, lastName] = preferLegalName(user);
    const cardFriendlyFirstName = toCardFriendlyName(firstName);
    const cardFriendlyLastName = toCardFriendlyName(lastName);
    const { userAddress } = user;

    return (
        <YStack
            gap={16}
            width="100%"
            backgroundColor="$gray200"
            borderRadius={16}
            padding={16}
        >
            <TitleText>Your Fruitful Physical Card details</TitleText>
            <YStack gap={8}>
                <XStack
                    justifyContent="space-between"
                    borderBottomWidth={1}
                    borderBottomColor="$gray500"
                    paddingBottom={8}
                    {...redactPrivate()}
                >
                    <Text color="$gray1000">Mailing address</Text>
                    <YStack alignItems="flex-end">
                        <Text>{userAddress?.line1}</Text>
                        {userAddress?.line2 != null &&
                        userAddress?.line2.length > 0 ? (
                            <Text>{userAddress?.line2}</Text>
                        ) : null}
                        <Text>
                            {userAddress?.city}, {userAddress?.state}{' '}
                            {userAddress?.postalCode}
                        </Text>
                    </YStack>
                </XStack>
                <XStack
                    justifyContent="space-between"
                    borderBottomWidth={1}
                    borderBottomColor="$gray500"
                    paddingBottom={8}
                >
                    <Text color="$gray1000">Arrives in mail</Text>
                    <Text>5-7 business days</Text>
                </XStack>
                <XStack justifyContent="space-between" paddingBottom={8}>
                    <Text color="$gray1000">Name on card</Text>
                    <Text>
                        {truncateCardFriendlyName(
                            cardFriendlyFirstName,
                            cardFriendlyLastName,
                        )}
                    </Text>
                </XStack>
            </YStack>
        </YStack>
    );
};

export const CardDetailsSection = ({
    accountsToOpen = [],
    accountsToAccept = [],
    user,
}: {
    accountsToOpen: MoneyMapNewFruitfulAccount[];
    accountsToAccept: MoneyMapNewFruitfulAccount[];
    user: User;
}) => {
    const needsPhysicalCard =
        accountsToOpen.some(
            (account) => account.createParameters?.physicalCard,
        ) ||
        accountsToAccept.some(
            (account) => account.createParameters?.physicalCard,
        );

    if (!needsPhysicalCard) {
        return null;
    }

    return <PhysicalCardDetails user={user} />;
};
